import { useEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react';

// Horizontal drag-to-scroll for the kiosk's overflow rails.
//
// The touchscreen doesn't reliably hand native horizontal pan gestures to an
// overflow scroller, so a finger drag left the rails stuck. This pans them
// directly off pointer deltas, and swallows the click that would otherwise
// fire on release after a real drag (otherwise scrolling past a chip
// activates it).
//
// Tracking happens on window listeners rather than setPointerCapture: capture
// makes the browser retarget the follow-up `click` to the capturing element,
// so buttons INSIDE the rail stop firing their onClick entirely — which is
// exactly how the light switches came to look dead on the kiosk.
//
// Spread `handlers` onto the scroller and attach `ref`; pair it with
// `overflow-x-auto touch-pan-x` (and `scrollbar-hidden` on the kiosk).
const DRAG_THRESHOLD_PX = 8;

export function useDragScroll() {
  const ref = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ startX: number; startScroll: number; moved: boolean } | null>(
    null,
  );
  // Listeners live for the duration of one drag; this holds the teardown so
  // an unmount mid-drag doesn't leak them.
  const detachRef = useRef<(() => void) | null>(null);
  useEffect(() => () => detachRef.current?.(), []);

  function beginDrag(clientX: number): void {
    const el = ref.current;
    if (!el) return;
    dragRef.current = { startX: clientX, startScroll: el.scrollLeft, moved: false };

    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!el || !drag) return;
      const dx = e.clientX - drag.startX;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX) drag.moved = true;
      if (drag.moved) {
        el.scrollLeft = drag.startScroll - dx;
        // Once this is a drag, stop the browser treating it as a text
        // selection / native scroll as well.
        e.preventDefault();
      }
    };
    const onUp = () => {
      detach();
      // Leave `moved` set so the click that follows this release can be
      // swallowed, then clear it.
      window.setTimeout(() => {
        dragRef.current = null;
      }, 0);
    };
    const detach = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      detachRef.current = null;
    };
    detachRef.current = detach;
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  return {
    ref,
    handlers: {
      onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
        beginDrag(e.clientX);
      },
      onClickCapture(e: ReactMouseEvent<HTMLDivElement>) {
        if (dragRef.current?.moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
    },
  };
}
